import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.js';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const logoUrl = window.TIFELOW_CHROME_LOGO_URL || '';
const contourUrl = window.TIFELOW_LOGO_CONTOURS_URL || '';

function makePath(points, canvasWidth, canvasHeight, width, height, path) {
  const first = points[0];
  path.moveTo((first[0] / canvasWidth - 0.5) * width, (0.5 - first[1] / canvasHeight) * height);
  for (let i = 1; i < points.length; i++) {
    path.lineTo((points[i][0] / canvasWidth - 0.5) * width, (0.5 - points[i][1] / canvasHeight) * height);
  }
  path.closePath();
  return path;
}

function makeExtrudedShapes(data, width, height) {
  return data.shapes.map(item => {
    const shape = makePath(item.outer, data.canvas[0], data.canvas[1], width, height, new THREE.Shape());
    for (const holePoints of item.holes || []) {
      const hole = makePath(holePoints, data.canvas[0], data.canvas[1], width, height, new THREE.Path());
      shape.holes.push(hole);
    }
    return shape;
  });
}

function mountLogo(el) {
  if (el.dataset.mounted === 'true') return;
  el.dataset.mounted = 'true';
  const fallback = el.querySelector('.tifelow-3d-fallback');
  let renderer, resizeObserver, raf;
  try {
    renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;
    renderer.domElement.setAttribute('aria-label', 'TIFELOW 3D-Chrome-Logo. Ziehen zum Drehen.');
    renderer.domElement.setAttribute('role', 'img');
    renderer.domElement.style.touchAction = 'none';
    el.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);
    camera.position.set(0, 0, 8.1);
    const group = new THREE.Group();
    scene.add(group);

    scene.add(new THREE.AmbientLight(0xffffff, 1.15));
    const key = new THREE.DirectionalLight(0xffffff, 3.4); key.position.set(-3, 4, 7); scene.add(key);
    const cool = new THREE.DirectionalLight(0x9bdfff, 2.3); cool.position.set(4, 1, -3); scene.add(cool);
    const pink = new THREE.DirectionalLight(0xffb7d9, 1.8); pink.position.set(-4, -2, 2); scene.add(pink);

    Promise.all([
      new Promise((resolve, reject) => new THREE.TextureLoader().load(logoUrl, resolve, undefined, reject)),
      fetch(contourUrl).then(r => { if (!r.ok) throw new Error('Logo-Konturen konnten nicht geladen werden'); return r.json(); })
    ]).then(([texture, contourData]) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), 12);
      texture.generateMipmaps = true;
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.magFilter = THREE.LinearFilter;
      const ratio = texture.image.width / texture.image.height;
      const width = 7.15, height = width / ratio;
      const depth = 0.24;
      const shapes = makeExtrudedShapes(contourData, width, height);

      // Actual extruded silhouette with cut-through counters, plus chrome bevelled sides.
      const sideUniforms = { uTime: { value: 0 } };
      const invisibleFace = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide });
      const sideChrome = new THREE.ShaderMaterial({
        uniforms: sideUniforms,
        transparent: false,
        side: THREE.DoubleSide,
        vertexShader: `
          varying vec3 vPos;
          varying vec3 vNormal;
          void main() {
            vPos = position;
            vNormal = normalize(normalMatrix * normal);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform float uTime;
          varying vec3 vPos;
          varying vec3 vNormal;
          void main() {
            float wave = sin(vPos.x * 7.0 + vPos.y * 3.0 + uTime * 1.7);
            float bands = smoothstep(-0.25, 0.10, wave) * (1.0 - smoothstep(0.45, 0.82, wave));
            float stripe = pow(max(0.0, sin(vPos.x * 4.5 - vPos.y * 5.0 + vPos.z * 25.0 + uTime * 2.1)), 10.0);
            float edge = 1.0 - abs(normalize(vNormal).z);
            vec3 darkChrome = vec3(0.055, 0.065, 0.085);
            vec3 silver = vec3(0.62, 0.68, 0.75);
            vec3 bright = vec3(1.0, 0.98, 0.96);
            vec3 color = mix(darkChrome, silver, bands);
            color = mix(color, bright, clamp(stripe * 0.95 + edge * 0.28, 0.0, 1.0));
            gl_FragColor = vec4(color, 1.0);
            #include <tonemapping_fragment>
            #include <colorspace_fragment>
          }
        `
      });
      const extrusion = new THREE.ExtrudeGeometry(shapes, {
        depth,
        steps: 1,
        bevelEnabled: true,
        bevelSegments: 3,
        bevelSize: 0.025,
        bevelThickness: 0.025,
        curveSegments: 4
      });
      extrusion.translate(0, 0, -depth / 2);
      group.add(new THREE.Mesh(extrusion, [invisibleFace, sideChrome]));

      const frontUniforms = { uMap: { value: texture }, uTime: { value: 0 } };
      const frontMaterial = new THREE.ShaderMaterial({
        uniforms: frontUniforms,
        transparent: true,
        alphaTest: 0.025,
        depthWrite: false,
        side: THREE.DoubleSide,
        vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform sampler2D uMap;
          uniform float uTime;
          varying vec2 vUv;
          void main() {
            vec4 texel = texture2D(uMap, vUv);
            if (texel.a < 0.025) discard;
            float phase = mod(uTime * 0.23, 1.65) - 0.30;
            float axis = vUv.x + (vUv.y - 0.5) * 0.26;
            float d = axis - phase;
            float core = exp(-pow(d * 35.0, 2.0));
            float halo = exp(-pow(d * 10.0, 2.0));
            float glint = clamp(core * 0.92 + halo * 0.28, 0.0, 1.0);
            vec3 coolSilver = vec3(0.82, 0.92, 1.0);
            vec3 roseSilver = vec3(1.0, 0.72, 0.86);
            vec3 tint = mix(coolSilver, roseSilver, smoothstep(0.1, 0.9, vUv.y));
            texel.rgb = mix(texel.rgb, tint, glint * 0.78);
            texel.rgb += vec3(0.12, 0.14, 0.18) * halo * 0.12;
            gl_FragColor = texel;
            #include <tonemapping_fragment>
            #include <colorspace_fragment>
          }
        `
      });
      const front = new THREE.Mesh(new THREE.PlaneGeometry(width, height), frontMaterial);
      front.position.z = depth / 2 + 0.035;
      group.add(front);

      // One reverse face only: the same logo mirrored naturally on the back, with the same moving chrome glint.
      const backTexture = texture.clone();
      backTexture.needsUpdate = true;
      backTexture.wrapS = THREE.ClampToEdgeWrapping;
      backTexture.repeat.x = -1;
      backTexture.offset.x = 1;
      const backUniforms = { uMap: { value: backTexture }, uTime: { value: 0 } };
      const backMaterial = frontMaterial.clone();
      backMaterial.uniforms = backUniforms;
      const back = new THREE.Mesh(new THREE.PlaneGeometry(width, height), backMaterial);
      back.position.z = -depth / 2 - 0.035;
      group.add(back);

      el._chromeUniforms = [frontUniforms, backUniforms, sideUniforms];
      el.classList.add('is-rendered');
      if (fallback) fallback.hidden = true;
      resize();
    }).catch(err => {
      console.error('TIFELOW 3D logo assets failed to load', err);
      if (renderer) renderer.domElement.remove();
      el.dataset.mounted = 'false';
    });

    const resize = () => {
      const width = Math.max(el.clientWidth, 1), height = Math.max(el.clientHeight, 1);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      // Keep the full extruded logo inside the viewport on desktop and mobile.
      // The source silhouette is tall; the former close camera clipped the top/bottom.
      camera.position.z = width < 520 ? 10.7 : width < 900 ? 10.3 : 10.0;
      camera.updateProjectionMatrix();
      const scale = width < 520 ? 0.94 : width < 900 ? 0.98 : 1.02;
      group.scale.setScalar(scale);
    };
    resizeObserver = new ResizeObserver(resize); resizeObserver.observe(el); resize();

    let dragging = false, lastX = 0, lastY = 0, lastAngle = 0, activePointers = new Map();
    const homeRotation = { x: -0.04, y: 0, z: 0 };
    let rotX = homeRotation.x, rotY = homeRotation.y, rotZ = homeRotation.z, lastInteraction = performance.now();
    let manuallyRotated = false, resetStartedAt = 0, resetFrom = null;
    const resetDuration = 1500;
    const angleDelta = (from, to) => Math.atan2(Math.sin(to - from), Math.cos(to - from));
    function angleBetweenPointers() {
      const pts = [...activePointers.values()];
      if (pts.length < 2) return null;
      return Math.atan2(pts[1].y - pts[0].y, pts[1].x - pts[0].x);
    }
    renderer.domElement.addEventListener('pointerdown', e => {
      renderer.domElement.setPointerCapture(e.pointerId);
      activePointers.set(e.pointerId, {x:e.clientX, y:e.clientY});
      dragging = true; lastX = e.clientX; lastY = e.clientY;
      lastAngle = angleBetweenPointers() ?? 0; lastInteraction = performance.now();
      renderer.domElement.classList.add('is-dragging');
    });
    renderer.domElement.addEventListener('pointermove', e => {
      if (!activePointers.has(e.pointerId)) return;
      activePointers.set(e.pointerId, {x:e.clientX, y:e.clientY});
      if (!dragging) return;
      if (activePointers.size >= 2) {
        const a = angleBetweenPointers();
        if (a !== null) { rotZ += a - lastAngle; lastAngle = a; }
      } else {
        const dx = e.clientX - lastX, dy = e.clientY - lastY;
        if (Math.abs(dx) + Math.abs(dy) > 0) manuallyRotated = true;
        rotY += dx * 0.012;
        rotX += dy * 0.012;
      }
      if (activePointers.size >= 2) manuallyRotated = true;
      lastX = e.clientX; lastY = e.clientY; lastInteraction = performance.now();
      resetStartedAt = 0; resetFrom = null;
    });
    const release = e => {
      activePointers.delete(e.pointerId);
      dragging = activePointers.size > 0;
      if (!dragging) renderer.domElement.classList.remove('is-dragging');
      lastAngle = angleBetweenPointers() ?? 0;
    };
    renderer.domElement.addEventListener('pointerup', release);
    renderer.domElement.addEventListener('pointercancel', release);
    renderer.domElement.addEventListener('lostpointercapture', release);
    // Deliberately do not bind wheel/mousewheel to rotation: scrolling the page must not spin the logo.
    renderer.domElement.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft') rotY -= .15;
      else if (e.key === 'ArrowRight') rotY += .15;
      else if (e.key === 'ArrowUp') rotX -= .15;
      else if (e.key === 'ArrowDown') rotX += .15;
      else if (e.key.toLowerCase() === 'z') rotZ += .15;
      else return;
      e.preventDefault(); manuallyRotated = true; lastInteraction = performance.now();
      resetStartedAt = 0; resetFrom = null;
    });
    renderer.domElement.tabIndex = 0;
    const clock = new THREE.Clock();
    function frame() {
      raf = requestAnimationFrame(frame);
      const delta = clock.getDelta();
      if (el._chromeUniforms) el._chromeUniforms.forEach(u => { u.uTime.value += delta; });
      const now = performance.now();
      if (!dragging && manuallyRotated && now - lastInteraction >= 5000 && !resetStartedAt) {
        resetStartedAt = now;
        resetFrom = { x: rotX, y: rotY, z: rotZ };
      }
      if (resetStartedAt && resetFrom) {
        const progress = Math.min(1, (now - resetStartedAt) / resetDuration);
        const eased = progress * progress * (3 - 2 * progress);
        rotX = resetFrom.x + (homeRotation.x - resetFrom.x) * eased;
        rotY = resetFrom.y + angleDelta(resetFrom.y, homeRotation.y) * eased;
        rotZ = resetFrom.z + angleDelta(resetFrom.z, homeRotation.z) * eased;
        if (progress >= 1) {
          rotX = homeRotation.x; rotY = homeRotation.y; rotZ = homeRotation.z;
          resetStartedAt = 0; resetFrom = null; manuallyRotated = false; lastInteraction = now;
        }
      } else if (!dragging && !reduceMotion && now - lastInteraction > 1800) {
        // Keep the logo's normal, subtle rotation while the 5-second idle timer runs.
        // When the timer expires, the smooth reset takes over from the current angle.
        rotY += delta * 0.32;
      }
      group.rotation.set(rotX, rotY, rotZ);
      renderer.render(scene, camera);
    }
    frame();
    el._tifelowCleanup = () => {
      cancelAnimationFrame(raf);
      if (resizeObserver) resizeObserver.disconnect();
      renderer.dispose();
    };
  } catch (err) {
    console.error('TIFELOW 3D logo init failed', err);
    if (fallback) fallback.hidden = false;
  }
}
document.querySelectorAll('[data-tifelow-3d]').forEach(mountLogo);
